import React from "react"
import Layout from "../components/Layout"

const skillGroups = [
  {
    title: "Programming & Data",
    skills: ["Python", "PyTorch", "NumPy", "SciPy", "scikit-learn", "MATLAB", "Git"],
  },
  {
    title: "Optimization & Control",
    skills: ["Pyomo", "GAMS", "LP", "NLP", "MILP", "MINLP", "PID", "Model Predictive Control"],
  },
  {
    title: "Machine Learning",
    skills: ["Physics-Informed Neural Networks", "Time-Series Modeling", "Regression", "Classification"],
  },
  {
    title: "Cloud & Software",
    skills: ["Databricks", "JavaScript", "React", "Node.js", "Express", "MySQL"],
  },
]

const ProjectTags = ({ tags }) => (
  <ul className="tags">
    {tags.map(tag => (
      <li key={tag}>{tag}</li>
    ))}
  </ul>
)

const publications = [
  "Z. Zhang, B. Huang, Z. Li. Distributionally robust optimization for Gaussian mixture model ambiguity under moment variations. Computers & Chemical Engineering (2026): 109833.",
  "S. Li, Z. Li, Z. Zhang, et al. Database-driven and property-constrained inference of molecular composition of petroleum fractions from routine experimental data[J]. Chemical Engineering Journal, 2026: 176448.",
  "Z. Zhang, B. Huang, Z. Li. Simultaneous Outlier-Exclusion and Distributionally Robust Learning through Partial Optimal Transport. Computers & Chemical Engineering (2025): 109408.",
  "S. Li, Z. Zhang, Z. Li, G. Cai, L. Zhang, Q. Shi. Molecular composition reconstruction of naphtha fractions through data-driven modeling and interpretable optimization. Chemical Engineering Science (2025): 122655.",
  "Z. Zhang, B. Huang, Z. Li. Dynamic Process Flexibility Analysis Using Neural Networks and a Volumetric Flexibility Index. Industrial & Engineering Chemistry Research. 2025, 64, 7452-7465.",
  "Z. Zhang, S. Yang, B. Huang, Z. Li. Novel Feasible Set Learning and Process Flexibility Analysis Method Using Deep Neural Networks. Industrial & Engineering Chemistry Research. 2024, 63, 11516-11529.",
  "Z. Zhang, and Z. Li. Model Predictive Control for Renal Anemia Treatment through Physics-informed Neural Network. IFAC-PapersOnLine 58.14 (2024): 307-312.",
  "Z. Zhang, Z. Li. Haemoglobin Response Modelling under Erythropoietin Treatment: Physiological Model-Informed Machine Learning Method. The Canadian Journal of Chemical Engineering. 2023, 101, 4307-4319.",
  "Z. Zhang, B. Huang, Z. Li. The Two Faces of Outliers in Robust Learning: Exclusion and Emphasis via Partial Optimal Transport. (Under Preparation).",
]

const presentations = [
  {
    title:
      "Distributionally Robust Optimization for Gaussian Mixture Model Ambiguity Under Moment Variations",
    detail:
      "Oral presentation in American Institute of Chemical Engineers (AIChE) Annual Meeting on November 3, 2025, Boston, Massachusetts, U.S.",
  },
  {
    title:
      "Decision-focused Learning Considering Solution Robustness under Data Uncertainty",
    detail:
      "Oral presentation in Canadian Chemical Engineering Conference (CSChE 2025) on October 7, 2025, Montreal, Quebec, Canada.",
  },
  {
    title: "Dynamic Process Operational Flexibility Analysis Using Neural Networks",
    detail:
      "Poster presentation in Canadian Chemical Engineering Conference (CSChE 2024) on October 6-9, 2024, Toronto, Ontario, Canada.",
  },
  {
    title:
      "Model Predictive Control for Renal Anemia Treatment through Physics-Informed Neural Network",
    detail:
      "Poster presentation in 12th IFAC International Symposium on Advanced Control of Chemical Processes (ADCHEM) on July 14-17, 2024, Toronto, Ontario, Canada.",
  },
  {
    title:
      "A Novel Volumetric Flexibility Index Calculation Method Using Physics-Informed Neural Networks",
    detail:
      "Oral presentation in American Institute of Chemical Engineers (AIChE) Annual Meeting on November 9, 2023, Orlando, Florida, U.S.",
  },
]

const awards = [
  "Graduate Students' Association Academic Travel Grant (2025)",
  "Captain Thomas Farrell Greenhalgh Memorial Graduate Scholarship (2023)",
  "Graduate Students' Association Academic Travel Grant (2023)",
]

const Resume = () => {
  return (
    <Layout>
      <article className="cv">
        <header>
          <p>CV(web)</p>
          <h1>Zhongyu Zhang</h1>
          <span>
            PhD candidate in process control, uncertainty-aware optimization,
            and data-driven process systems engineering.
          </span>
        </header>

        <section>
          <h2>Links</h2>
          <p>
            <a
              href="https://scholar.google.com/citations?user=XjUxktcAAAAJ&hl=en&oi=ao"
              target="_blank"
              rel="noreferrer"
            >
              Google Scholar
            </a>{" "}
            /{" "}
            <a
              href="https://www.linkedin.com/in/zhongyu-zhang-profile"
              target="_blank"
              rel="noreferrer"
            >
              LinkedIn
            </a>{" "}
            / <a href="mailto:zhongyu4@ualberta.ca">zhongyu4@ualberta.ca</a> /{" "}
            <a href="https://github.com/zzygith" target="_blank" rel="noreferrer">
              GitHub
            </a>
          </p>
        </section>

        <section>
          <h2>Education</h2>
          <div className="entry">
            <div>
              <h3>University of Alberta</h3>
              <p>
                PhD candidate, Process Control. Supervised by Prof. Zukui Li
                and Prof. Biao Huang.
              </p>
            </div>
            <span>2023.01 - 2026.12 / Edmonton, Alberta, Canada</span>
          </div>
          <div className="entry">
            <div>
              <h3>University of Alberta</h3>
              <p>
                MSc Degree, Process Control. GPA: 3.8/4.0. Supervised by Prof.
                Zukui Li.
              </p>
            </div>
            <span>2020.09 - 2022.12 / Edmonton, Alberta, Canada</span>
          </div>
          <div className="entry">
            <div>
              <h3>Shandong University</h3>
              <p>BEng Degree, Automation. GPA: 4.17/5.</p>
            </div>
            <span>2016.09 - 2020.06 / Jinan, Shandong, China</span>
          </div>
        </section>

        <section>
          <h2>Technical Skills</h2>
          <div className="skill-groups">
            {skillGroups.map(group => (
              <div className="skill-group" key={group.title}>
                <h3>{group.title}</h3>
                <ul className="tags">
                  {group.skills.map(skill => (
                    <li key={skill}>{skill}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        <section>
          <h2>Industrial Experience</h2>
          <div className="cv-block">
            <h3>DCS Laboratory Development and Industrial Training</h3>
            <p>May 2026 - Present / SUPCON Technology</p>
            <ProjectTags
              tags={[
                "SUPCON ECS-700",
                "Distributed Control Systems (DCS)",
                "Process Control",
                "PID",
                "Cascade Control",
              ]}
            />
            <ul>
              <li>
                Completed hands-on training led by SUPCON engineers and
                participated in on-site commissioning of the ECS-700 industrial
                distributed control system and coupled-tank level-control
                hardware.
              </li>
              <li>
                Collaborating with the laboratory team to design course
                experiments that teach DCS fundamentals, engineering software
                configuration, and closed-loop process control using the
                industrial platform.
              </li>
            </ul>
          </div>

          <div className="cv-block">
            <h3>Student Researcher</h3>
            <p>Feb. 2024 - Jun. 2026 / Teck Resources Limited</p>
            <h4>KIVCET Furnace Feed Optimization (Jan. 2025 - Jun. 2026)</h4>
            <ProjectTags
              tags={[
                "Robust Optimization",
                "Soft Sensor Modeling",
                "Neural Networks",
                "NLP",
                "SLSQP Solver",
                "Databricks",
              ]}
            />
            <ul>
              <li>
                Collaborated with senior process control engineers at Trail
                Smelter to develop a Databricks-based decision-support pipeline
                combining plant data, machine-learning estimates of residue
                composition, and optimization to recommend furnace feed
                adjustments.
              </li>
              <li>
                Translated operational requirements into an uncertainty-aware
                optimization model balancing residue utilization, feed moisture,
                chemistry targets, and equipment constraints.
              </li>
              <li>
                Simulations improved feed element-ratio target compliance by
                63% and increased monthly residue utilization by several hundred
                tonnes versus manual operation.
              </li>
            </ul>

            <h4>MPC Model Health Monitoring (Feb. 2024 - Jan. 2025)</h4>
            <ProjectTags
              tags={[
                "Model Predictive Control (MPC)",
                "System Identification",
                "Correlation Analysis",
                "Frequency Response",
                "SVM",
                "Databricks",
              ]}
            />
            <ul>
              <li>
                Worked with Teck engineers to migrate a model-plant mismatch
                monitoring toolbox from a local application to Databricks and
                integrate it into daily reporting for two industrial MPC
                systems.
              </li>
              <li>
                Combined five complementary analytics methods to detect when
                controller models no longer matched plant behaviour and identify
                the affected input-output relationships.
              </li>
              <li>
                Automated data processing and interactive HTML reporting to
                present operating KPIs, process trends, and model-health alerts
                in one place for engineering review.
              </li>
            </ul>
          </div>
        </section>

        <section>
          <h2>Research Projects</h2>
          <div className="cv-block">
            <h3>Robust Machine Learning for Corrupted and Underrepresented Data</h3>
            <p>2025 - Present</p>
            <ProjectTags
              tags={[
                "Optimization under Uncertainty",
                "Python",
                "LP",
                "NLP",
                "Pyomo",
                "GAMS",
                "CBC Solver",
                "CONOPT Solver",
              ]}
            />
            <ul>
              <li>
                Developed two complementary robust-learning methods that either
                filter unreliable samples or emphasize rare but valuable
                operating conditions using partial optimal transport and
                distributionally robust optimization.
              </li>
              <li>
                For corrupted measurements and labels, automatically excluded
                unreliable samples, improving process fault-classification
                accuracy from 57% to 77% and reducing soft-sensor RMSE by 57%.
              </li>
              <li>
                For underrepresented or shifted operating regimes, emphasized
                difficult but informative samples, reducing degraded-regime
                prediction RMSE by 37% and improving sensor-drift classification
                accuracy from 68% to 73%.
              </li>
            </ul>
          </div>

          <div className="cv-block">
            <h3>Data-Driven Petroleum Composition Inference from Routine Measurements</h3>
            <p>2024 - 2026</p>
            <ProjectTags
              tags={[
                "Molecular Composition Analysis",
                "Python",
                "Neural Networks",
                "NLP",
                "Pyomo",
                "OMLT",
                "IPOPT Solver",
              ]}
            />
            <ul>
              <li>
                Co-developed data-driven methods to infer detailed molecular
                compositions of petroleum streams from limited, routinely
                available laboratory measurements, reducing reliance on costly
                specialized analysis.
              </li>
              <li>
                Combined neural networks, database matching, and
                property-constrained optimization to reconstruct chemically
                realistic compositions for both naphtha and heavy gas-oil
                fractions.
              </li>
              <li>
                Achieved an average R2 of 0.957 across 44 naphtha test samples;
                on 21 heavy gas-oil test samples, the inferred molecular
                patterns closely matched high-resolution measurements while
                reproducing key bulk properties near routine measurement
                accuracy.
              </li>
            </ul>
          </div>

          <div className="cv-block">
            <h3>Quantifying Process Tolerance to Uncertainty with Neural Networks</h3>
            <p>2023 - 2025</p>
            <ProjectTags
              tags={[
                "Physics-Informed Neural Networks (PINNs)",
                "Python",
                "DeepXDE",
                "CNN",
                "NLP",
                "Pyomo",
                "OMLT",
                "IPOPT Solver",
              ]}
            />
            <ul>
              <li>
                Developed neural-network methods to map constraint-satisfying
                operating regions and quantify how much variation in process
                conditions a system can tolerate without violating operating
                limits.
              </li>
              <li>
                Extended the approach from steady-state systems to time-varying
                processes, using physics-informed surrogate models and
                classifiers to handle nonlinear, disconnected, and
                high-dimensional operating regions.
              </li>
              <li>
                Achieved up to 27x faster evaluation than conventional
                solver-based methods in benchmark process case studies, while
                maintaining over 95% accuracy in classifying feasible operating
                trajectories.
              </li>
            </ul>
          </div>

          <div className="cv-block">
            <h3>Physics-Informed Modeling and Dose Optimization for Renal Anemia Treatment</h3>
            <p>2022 - 2024</p>
            <ProjectTags
              tags={[
                "Model Predictive Control (MPC)",
                "Physics-Informed Neural Networks (PINNs)",
                "Python",
                "DeepXDE",
                "LSTM",
                "NLP",
                "IPOPT Solver",
              ]}
            />
            <ul>
              <li>
                Developed a physics-informed neural network that combines sparse
                clinical data with physiological equations to estimate
                patient-specific parameters and predict haemoglobin response to
                EPO treatment; evaluated using records from 291 patients.
              </li>
              <li>
                Converted the personalized model into a fast control-oriented
                surrogate and integrated it with zone model predictive control
                to optimize EPO dose schedules while maintaining haemoglobin
                within a target range.
              </li>
              <li>
                Reduced prediction RMSE by 44-48% compared with LSTM and
                restored haemoglobin to the target range following simulated
                bleeding and infection disturbances in a two-year virtual-patient
                study.
              </li>
            </ul>
          </div>
        </section>

        <section>
          <h2>Publications</h2>
          <ol className="cv-list">
            {publications.map(publication => (
              <li key={publication}>{publication}</li>
            ))}
          </ol>
        </section>

        <section>
          <h2>Presentations</h2>
          {presentations.map(presentation => (
            <div className="cv-block" key={presentation.title}>
              <h3>{presentation.title}</h3>
              <p>{presentation.detail}</p>
            </div>
          ))}
        </section>

        <section>
          <h2>Awards</h2>
          <ul className="cv-list">
            {awards.map(award => (
              <li key={award}>{award}</li>
            ))}
          </ul>
        </section>

        <section>
          <h2>Teaching Assistant Experience</h2>
          <ul className="cv-list">
            <li>CHE 574 Process Optimization (Winter 2026)</li>
            <li>
              CHE 358 Process Data Analytics and Machine Learning
              (Spring/Summer 2024)
            </li>
          </ul>
        </section>
      </article>
    </Layout>
  )
}

export default Resume
